import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, TrendingUpIcon, ZapIcon, TargetIcon, UsersIcon, BuildingIcon, SparklesIcon, BrainIcon, DatabaseIcon, PlusIcon, ShieldCheckIcon, NetworkIcon } from 'lucide-react';
import { mockPlaysNew } from '../mockData';
export function PlaysLibraryPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStage, setSelectedStage] = useState<string[]>([]);
  const [selectedMotion, setSelectedMotion] = useState<string[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<string[]>([]);
  const [selectedAccountTier, setSelectedAccountTier] = useState<string[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string[]>([]);
  const [selectedZoomInfoFeature, setSelectedZoomInfoFeature] = useState<string[]>([]);
  const [selectedComplexity, setSelectedComplexity] = useState<string[]>([]);
  const [selectedAIReadiness, setSelectedAIReadiness] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'curated' | 'browse' | 'your-plays'>('curated');
  const stages = ['Awareness', 'Engagement', 'Evaluation', 'Negotiation', 'Commit', 'Onboarding', 'Activation', 'Impact', 'Expansion'];
  const motions = ['Inbound', 'Outbound', 'ABM', 'Event', 'PLG', 'Partner', 'Success'];
  const personas = ['CIO', 'CISO', 'CFO/Finance', 'IT Ops', 'Marketing', 'Sales', 'Procurement', 'VP Engineering', 'VP Operations'];
  const accountTiers = ['Tier 1', 'Tier 2', 'Longtail'];
  const channels = ['Email', 'Ads', 'LinkedIn', 'Phone', 'Direct Mail', 'Web', 'In-app'];
  const zoomInfoFeatures = ['Intent', 'Enrichment', 'Buying Groups', 'Account Summaries', 'Copilot Chat', 'Predictive', 'Technographics', 'Org Charts'];
  const complexities = ['simple', 'standard', 'advanced'];
  const aiReadiness = ['native', 'partial', 'manual'];
  const curatedSets = [{
    id: 'zoominfo-recommended',
    title: 'ZoomInfo Recommended',
    description: '16 proven plays across the full GTM funnel',
    count: 8,
    icon: '🎯',
    plays: mockPlaysNew.filter(p => p.source === 'zoominfo')
  }, {
    id: 'signal-first',
    title: 'Signal-First Plays',
    description: 'Intent, behavior, product use, and event triggers',
    count: 6,
    icon: '📡',
    plays: mockPlaysNew.filter(p => p.zoominfo_features.some(f => ['Intent', 'Predictive'].includes(f)))
  }, {
    id: 'event-field',
    title: 'Event & Field',
    description: 'Pre, during, and post-event orchestration',
    count: 4,
    icon: '🎪',
    plays: mockPlaysNew.filter(p => p.motion.includes('Event'))
  }, {
    id: 'expansion-ai',
    title: 'Expansion & AI Attach',
    description: 'Customer growth and AI product upsells',
    count: 3,
    icon: '📈',
    plays: mockPlaysNew.filter(p => p.stage === 'Expansion')
  }, {
    id: 'recovery',
    title: 'Recovery Plays',
    description: 'Leaky funnel and stalled opportunities',
    count: 2,
    icon: '🔄',
    plays: mockPlaysNew.filter(p => p.title.toLowerCase().includes('renewal'))
  }];
  const filteredPlays = mockPlaysNew.filter(play => {
    const matchesSearch = play.title.toLowerCase().includes(searchQuery.toLowerCase()) || play.summary.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = selectedStage.length === 0 || selectedStage.includes(play.stage);
    const matchesMotion = selectedMotion.length === 0 || play.motion.some(m => selectedMotion.includes(m));
    const matchesPersona = selectedPersona.length === 0 || play.personas.some(p => selectedPersona.includes(p));
    const matchesAccountTier = selectedAccountTier.length === 0 || play.account_tier.some(t => selectedAccountTier.includes(t));
    const matchesChannel = selectedChannel.length === 0 || play.channels.some(c => selectedChannel.includes(c));
    const matchesZoomInfoFeature = selectedZoomInfoFeature.length === 0 || play.zoominfo_features.some(f => selectedZoomInfoFeature.includes(f));
    const matchesComplexity = selectedComplexity.length === 0 || selectedComplexity.includes(play.complexity);
    const matchesAIReadiness = selectedAIReadiness.length === 0 || selectedAIReadiness.includes(play.ai_readiness);
    return matchesSearch && matchesStage && matchesMotion && matchesPersona && matchesAccountTier && matchesChannel && matchesZoomInfoFeature && matchesComplexity && matchesAIReadiness;
  });
  const toggleFilter = (value: string, selected: string[], setter: (value: string[]) => void) => {
    if (selected.includes(value)) {
      setter(selected.filter(v => v !== value));
    } else {
      setter([...selected, value]);
    }
  };
  const FilterButton = ({
    label,
    isSelected,
    onClick
  }: {
    label: string;
    isSelected: boolean;
    onClick: () => void;
  }) => <button onClick={onClick} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${isSelected ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-6012">
      {label}
    </button>;
  const getFeatureIcon = (feature: string) => {
    const icons: Record<string, React.ReactNode> = {
      Intent: <TrendingUpIcon className="w-4 h-4" data-id="element-6013" />,
      Enrichment: <DatabaseIcon className="w-4 h-4" data-id="element-6014" />,
      'Buying Groups': <UsersIcon className="w-4 h-4" data-id="element-6015" />,
      'Account Summaries': <TargetIcon className="w-4 h-4" data-id="element-6016" />,
      'Copilot Chat': <SparklesIcon className="w-4 h-4" data-id="element-6017" />,
      Predictive: <BrainIcon className="w-4 h-4" data-id="element-6018" />,
      Technographics: <BuildingIcon className="w-4 h-4" data-id="element-6019" />,
      'Org Charts': <UsersIcon className="w-4 h-4" data-id="element-6020" />
    };
    return icons[feature] || <ZapIcon className="w-4 h-4" data-id="element-6021" />;
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-6022">
      {/* Hero */}
      <div className="bg-gradient-to-br from-red-600 to-red-700 text-white px-6 py-12" data-id="element-6023">
        <div className="max-w-7xl mx-auto" data-id="element-6024">
          <h1 className="text-4xl font-bold mb-4" data-id="element-6025">
            Plays that run on ZoomInfo data and AI
          </h1>
          <p className="text-xl text-red-100 mb-6" data-id="element-6026">
            Studio plans the plays. Copilot Agents run the plays. Outcomes teach
            the next play.
          </p>
          <div className="flex items-center gap-4" data-id="element-6027">
            <button onClick={() => navigate('/gtm-studio/workspace')} className="px-6 py-3 bg-white text-red-600 rounded-lg hover:bg-red-50 font-medium transition-colors" data-id="element-6028">
              Launch in Studio
            </button>
            <button onClick={() => navigate('/programs/overview')} className="px-6 py-3 border-2 border-white text-white rounded-lg hover:bg-white/10 font-medium transition-colors" data-id="element-6029">
              Add to Program
            </button>
          </div>
        </div>
      </div>
      {/* Powered by Platform Band */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-6030">
        <div className="max-w-7xl mx-auto" data-id="element-6031">
          <p className="text-sm text-gray-600 mb-3" data-id="element-6032">
            These plays are built to run on:
          </p>
          <div className="flex items-center gap-4 flex-wrap" data-id="element-6033">
            <button onClick={() => navigate('/gtm-studio/workspace')} className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors" data-id="element-6034">
              <TargetIcon className="w-4 h-4" data-id="element-6035" />
              <span className="text-sm font-medium" data-id="element-6036">GTM Studio</span>
            </button>
            <button onClick={() => navigate('/ai-config/overview')} className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-6037">
              <SparklesIcon className="w-4 h-4" data-id="element-6038" />
              <span className="text-sm font-medium" data-id="element-6039">Copilot Agents</span>
            </button>
            <button onClick={() => navigate('/orchestration/journey-map')} className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors" data-id="element-6040">
              <ZapIcon className="w-4 h-4" data-id="element-6041" />
              <span className="text-sm font-medium" data-id="element-6042">Orchestration</span>
            </button>
            <button onClick={() => navigate('/integrations/catalog')} className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors" data-id="element-6043">
              <BuildingIcon className="w-4 h-4" data-id="element-6044" />
              <span className="text-sm font-medium" data-id="element-6045">Integrations</span>
            </button>
            <button onClick={() => navigate('/planning/forecasting')} className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors" data-id="element-6046">
              <TrendingUpIcon className="w-4 h-4" data-id="element-6047" />
              <span className="text-sm font-medium" data-id="element-6048">Forecast</span>
            </button>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6" data-id="element-6049">
        <div className="max-w-7xl mx-auto flex items-center gap-6" data-id="element-6050">
          <button onClick={() => setActiveTab('curated')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'curated' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-6051">
            Curated Sets
          </button>
          <button onClick={() => setActiveTab('browse')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'browse' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-6052">
            Browse All
          </button>
          <button onClick={() => setActiveTab('your-plays')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'your-plays' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-6053">
            Your Plays
          </button>
          <div className="flex-1" data-id="element-6054" />
          <button onClick={() => navigate('/plays/authoring')} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-6055">
            <PlusIcon className="w-4 h-4" data-id="element-6056" />
            New Play
          </button>
        </div>
      </div>
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" data-id="element-6057">
        {/* Left Filters (Browse All only) */}
        {activeTab === 'browse' && <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto p-6 space-y-6" data-id="element-6058">
            {/* Search */}
            <div className="relative" data-id="element-6059">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-6060" />
              <input type="text" placeholder="Search plays..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-6061" />
            </div>
            {/* Filters */}
            <div data-id="element-6062">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-6063">
                Stage
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6064">
                {stages.map(stage => <FilterButton key={stage} label={stage} isSelected={selectedStage.includes(stage)} onClick={() => toggleFilter(stage, selectedStage, setSelectedStage)} data-id="element-6065" />)}
              </div>
            </div>
            <div data-id="element-6066">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-6067">
                Motion
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6068">
                {motions.map(motion => <FilterButton key={motion} label={motion} isSelected={selectedMotion.includes(motion)} onClick={() => toggleFilter(motion, selectedMotion, setSelectedMotion)} data-id="element-6069" />)}
              </div>
            </div>
            <div data-id="element-6070">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-6071">
                Persona
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6072">
                {personas.map(persona => <FilterButton key={persona} label={persona} isSelected={selectedPersona.includes(persona)} onClick={() => toggleFilter(persona, selectedPersona, setSelectedPersona)} data-id="element-6073" />)}
              </div>
            </div>
            <div data-id="element-6074">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-6075">
                Account Tier
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6076">
                {accountTiers.map(tier => <FilterButton key={tier} label={tier} isSelected={selectedAccountTier.includes(tier)} onClick={() => toggleFilter(tier, selectedAccountTier, setSelectedAccountTier)} data-id="element-6077" />)}
              </div>
            </div>
            <div data-id="element-6078">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-6079">
                Channel Mix
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6080">
                {channels.map(channel => <FilterButton key={channel} label={channel} isSelected={selectedChannel.includes(channel)} onClick={() => toggleFilter(channel, selectedChannel, setSelectedChannel)} data-id="element-6081" />)}
              </div>
            </div>
            <div data-id="element-6082">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-6083">
                ZoomInfo Features
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6084">
                {zoomInfoFeatures.map(feature => <FilterButton key={feature} label={feature} isSelected={selectedZoomInfoFeature.includes(feature)} onClick={() => toggleFilter(feature, selectedZoomInfoFeature, setSelectedZoomInfoFeature)} data-id="element-6085" />)}
              </div>
            </div>
            <div data-id="element-6086">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-6087">
                Complexity
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6088">
                {complexities.map(complexity => <FilterButton key={complexity} label={complexity} isSelected={selectedComplexity.includes(complexity)} onClick={() => toggleFilter(complexity, selectedComplexity, setSelectedComplexity)} data-id="element-6089" />)}
              </div>
            </div>
            <div data-id="element-6090">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-6091">
                AI Readiness
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6092">
                {aiReadiness.map(readiness => <FilterButton key={readiness} label={readiness} isSelected={selectedAIReadiness.includes(readiness)} onClick={() => toggleFilter(readiness, selectedAIReadiness, setSelectedAIReadiness)} data-id="element-6093" />)}
              </div>
            </div>
          </div>}
        {/* Center Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-6094">
          <div className="max-w-7xl mx-auto" data-id="element-6095">
            {activeTab === 'curated' && <div className="space-y-8" data-id="element-6096">
                {curatedSets.map(set => <div key={set.id} data-id="element-6097">
                    <div className="flex items-center justify-between mb-4" data-id="element-6098">
                      <div className="flex items-center gap-3" data-id="element-6099">
                        <span className="text-3xl" data-id="element-6100">{set.icon}</span>
                        <div data-id="element-6101">
                          <h2 className="text-xl font-bold text-gray-900" data-id="element-6102">
                            {set.title}
                          </h2>
                          <p className="text-sm text-gray-600" data-id="element-6103">
                            {set.description}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-6104">
                        {set.count} plays
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6105">
                      {set.plays.map(play => <button key={play.id} onClick={() => navigate(`/plays/${play.id}`)} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-lg transition-shadow text-left" data-id="element-6106">
                          <div className="flex items-start justify-between mb-3" data-id="element-6107">
                            <div className="flex-1" data-id="element-6108">
                              <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-6109">
                                {play.title}
                              </h3>
                              <p className="text-sm text-gray-600 line-clamp-2" data-id="element-6110">
                                {play.summary}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3" data-id="element-6111">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded" data-id="element-6112">
                              {play.stage}
                            </span>
                            {play.motion.slice(0, 2).map(motion => <span key={motion} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded" data-id="element-6113">
                                {motion}
                              </span>)}
                            {play.source === 'zoominfo' && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded" data-id="element-6114">
                                From ZoomInfo
                              </span>}
                          </div>
                          <div className="flex items-center gap-2 mb-3" data-id="element-6115">
                            {play.zoominfo_features.slice(0, 4).map(feature => <div key={feature} className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-gray-600" title={feature} data-id="element-6116">
                                  {getFeatureIcon(feature)}
                                </div>)}
                          </div>
                          <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-xs" data-id="element-6117">
                            <TrendingUpIcon className="w-3 h-3 text-green-600" data-id="element-6118" />
                            <span className="text-green-700 font-medium" data-id="element-6119">
                              {play.measurement.estimated_impact}
                            </span>
                          </div>
                        </button>)}
                    </div>
                  </div>)}
              </div>}
            {activeTab === 'browse' && <div data-id="element-6120">
                <div className="flex items-center justify-between mb-6" data-id="element-6121">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-6122">
                    All Plays ({filteredPlays.length})
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6123">
                  {filteredPlays.map(play => <button key={play.id} onClick={() => navigate(`/plays/${play.id}`)} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-lg transition-shadow text-left" data-id="element-6124">
                      <div className="flex items-start justify-between mb-3" data-id="element-6125">
                        <div className="flex-1" data-id="element-6126">
                          <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-6127">
                            {play.title}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-2" data-id="element-6128">
                            {play.summary}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3" data-id="element-6129">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded" data-id="element-6130">
                          {play.stage}
                        </span>
                        {play.motion.slice(0, 2).map(motion => <span key={motion} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded" data-id="element-6131">
                            {motion}
                          </span>)}
                        {play.source === 'zoominfo' && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded" data-id="element-6132">
                            From ZoomInfo
                          </span>}
                      </div>
                      <div className="mb-3" data-id="element-6133">
                        <p className="text-xs text-gray-600 mb-1" data-id="element-6134">
                          Signal Requirements:
                        </p>
                        <p className="text-xs text-gray-900 font-medium" data-id="element-6135">
                          {play.trigger.logic_md}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mb-3" data-id="element-6136">
                        {play.zoominfo_features.slice(0, 4).map(feature => <div key={feature} className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-gray-600" title={feature} data-id="element-6137">
                            {getFeatureIcon(feature)}
                          </div>)}
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-xs" data-id="element-6138">
                        <TrendingUpIcon className="w-3 h-3 text-green-600" data-id="element-6139" />
                        <span className="text-green-700 font-medium" data-id="element-6140">
                          {play.measurement.estimated_impact}
                        </span>
                      </div>
                    </button>)}
                </div>
              </div>}
            {activeTab === 'your-plays' && <div className="text-center py-16" data-id="element-6141">
                <p className="text-gray-600 mb-4" data-id="element-6142">
                  Your custom plays will appear here
                </p>
                <button onClick={() => navigate('/plays/authoring')} className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-6143">
                  Create Your First Play
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}